import React from 'react';
import { Box } from '@mui/material';
import { keyframes } from '@mui/system';

// Keyframes para animações
export const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

export const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

export const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
`;

export const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

// Componentes de animação
export const FadeIn = ({ children, delay = 0, duration = 0.5, ...props }) => (
  <Box
    sx={{
      animation: `${fadeIn} ${duration}s ease-out ${delay}s both`,
    }}
    {...props}
  >
    {children}
  </Box>
);

export const SlideIn = ({ children, delay = 0, duration = 0.5, direction = 'left', ...props }) => {
  const slideDirection = direction === 'right' ? 'translateX(20px)' : 'translateX(-20px)';

  return (
    <Box
      sx={{
        animation: `${keyframes`
          from {
            opacity: 0;
            transform: ${slideDirection};
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        `} ${duration}s ease-out ${delay}s both`,
      }}
      {...props}
    >
      {children}
    </Box>
  );
};

export const ScaleIn = ({ children, delay = 0, duration = 0.5, ...props }) => (
  <Box
    sx={{
      animation: `${scaleIn} ${duration}s ease-out ${delay}s both`,
    }}
    {...props}
  >
    {children}
  </Box>
);

export const Pulse = ({ children, ...props }) => (
  <Box
    sx={{
      animation: `${pulse} 2s infinite`,
    }}
    {...props}
  >
    {children}
  </Box>
);

// Loading shimmer effect
export const ShimmerBox = ({ width = '100%', height = 20, ...props }) => (
  <Box
    sx={{
      width,
      height,
      background: `linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)`,
      backgroundSize: '200px 100%',
      animation: `${shimmer} 1.5s infinite`,
      borderRadius: 1,
    }}
    {...props}
  />
);

// Staggered animation container
export const StaggeredFadeIn = ({ children, stagger = 0.1, ...props }) => {
  const childrenArray = React.Children.toArray(children);

  return (
    <Box {...props}>
      {childrenArray.map((child, index) => (
        <FadeIn key={index} delay={index * stagger}>
          {child}
        </FadeIn>
      ))}
    </Box>
  );
};

// Hover animation wrapper
export const HoverScale = ({ children, scale = 1.02, ...props }) => (
  <Box
    sx={{
      transition: 'transform 0.2s ease-in-out',
      '&:hover': {
        transform: `scale(${scale})`,
      },
    }}
    {...props}
  >
    {children}
  </Box>
);

// Bounce animation for notifications
export const BounceIn = ({ children, delay = 0, duration = 0.6, ...props }) => (
  <Box
    sx={{
      animation: `${keyframes`
        0% {
          opacity: 0;
          transform: scale(0.3);
        }
        50% {
          opacity: 1;
          transform: scale(1.05);
        }
        70% {
          transform: scale(0.9);
        }
        100% {
          opacity: 1;
          transform: scale(1);
        }
      `} ${duration}s ease-out ${delay}s both`,
    }}
    {...props}
  >
    {children}
  </Box>
);

export default {
  FadeIn,
  SlideIn,
  ScaleIn,
  Pulse,
  ShimmerBox,
  StaggeredFadeIn,
  HoverScale,
  BounceIn
};
